<template>
  <div class="tree-slider">
    <!-- Header with arrow up and filter -->
    <div
      class="tool-bar d-flex align-start"
      :style="{
        width: xs ? '100vw' : '100%',
        height: '43px',
        position: 'sticky !important',
      }"
    >
      <div ref="treeHeader" class="tree-header d-flex ma-0 pa-0 full-width background lighten-2">
        <div class="filter-container ma-0 pa-0">
          <div class="d-flex flex-row">
            <navigate-up
              :style="{ width: '100%' }"
              :showFilter="!this.disableTagsAndLabelFiltering && !hideFilter"
              :isFilterActive="isFilterActive"
              :isFilterVisible="isFilterVisible"
              :path="path()"
              :root-text-key="rootTextKey"
              :breadcrumb="breadcrumbPath"
              @oneLevelBack="oneLevelBack()"
              @toggleFilter="onToggleFilter"
            >
            </navigate-up>
          </div>
        </div>
      </div>
    </div>

    <v-expand-transition>
      <div v-if="isFilterVisible" :style="`border-bottom:1px solid #${darkMode ? '101010' : 'd0d0d0'} !important`">
        <div class="d-flex flex-row full-width">
          <v-text-field ref="filterInput" v-model="filter" class="px-3 pt-2 ma-0 mb-3" :placeholder="$t('folderFilterHint')" hide-details></v-text-field>
        </div>
        <div v-if="hasTagFilter && usedTags.length > 0" class="d-flex flex-row full-width px-3 ma-0 my-1">
          <tags-selection :tags="usedTags"></tags-selection>
        </div>
      </div>
    </v-expand-transition>

    <!-- Pagination -->
    <div
      v-if="files.length > filePageSize"
      class="paging d-flex align-center justify-center justify-space-around"
      :style="`top:calc(100vh - ${isToolbarsHidden ? 125 : 220}px) ;width:${xs ? 'calc(100vw - 20px)' : '100%'};left:10px;opacity:0.8;background:#404040`"
    >
      <v-btn icon color="white" :disabled="filePage === 0" @click="filePage = 0">
        <v-icon>mdi-chevron-double-left</v-icon>
      </v-btn>
      <v-btn icon color="white" :disabled="filePage === 0" @click="filePage--">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <n-list-title :text="pagingText"></n-list-title>
      <v-btn icon color="white" :disabled="filePage >= lastFilePage" @click="filePage++">
        <v-icon dark>mdi-chevron-right</v-icon>
      </v-btn>
      <v-btn icon color="white" :disabled="filePage >= lastFilePage" @click="filePage = lastFilePage">
        <v-icon dark>mdi-chevron-double-right</v-icon>
      </v-btn>
    </div>

    <transition :name="transition">
      <div v-if="showFolders" :style="`transition:all 0.5s ease-in-out`">
        <div>
          <!-- Folders -->
          <div v-for="folder in folders" :key="folder.id" @click.stop.prevent="selectFolder(folder)">
            <slot name="folder" v-bind:folder="folder">
              <!-- Fallback content -->
              {{ folder.id }} {{ folder.name }}Fallback content
            </slot>
          </div>
          <!-- Files -->
          <div v-for="(item, ind) in filesPage" :key="ind + '-' + item.id" @click.stop.prevent="selectFile(item)">
            <slot name="item" v-bind:item="item">
              <!-- Fallback content -->
              {{ item.id }} {{ item.filename }}Fallback content
            </slot>
          </div>
          <div class="pa-5 caption" v-if="!this.isFilterVisible && folders.length === 0 && filesPage.length === 0">
            {{ $t('noFolderItems') }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import NListTitle from '@/components/texts/NListTitle';
import common from '@/mixins/common';
import NavigateUp from './NavigateUp.vue';
import TagsSelection from '../../modules/document/TagsSelection.vue';
import sortFunctions from '@/sorters/sortFunctions';

export default {
  name: 'TreeSlider',
  mixins: [common],
  emits: ['onPathChanged', 'onFolderSelected', 'onFileSelected'],
  components: {
    NListTitle,
    NavigateUp,
    TagsSelection,
  },
  props: {
    items: { type: Array, required: true },
    currentFolder: Object,
    folderParentIdAttribute: { type: String, default: 'parentId' }, // attribute in a folder object containing the id of the parent folder or 0 to indicate the root
    folderType: String, // the value of the type attribute to mark a folder object
    rootTextKey: String, // the text key to be used when the navigateUp component is displayed within the root of a tree

    foldersById: Object, // Object with folders by id
    itemsById: Object, // eg. documentsById
    tags: { type: Array, required: false },
    labels: { type: Array, required: false },
    childrenType: { type: Array, required: true },
    disableTagsAndLabelFiltering: { type: Boolean, default: false },
    showFilesInRoot: { type: Boolean, default: true },
    hideFilter: { type: Boolean, default: true },
    sorterName: { type: String, default: 'byImportance' },
    breadcrumb: String,
  },
  data() {
    return {
      showFolders: true,
      transition: 'slide-right',
      isFilterVisible: false,
      filter: '',
      windowHeight: 0,
      currentFolder_: this.currentFolder,

      filePage: 0,
      filePageSize: 400,
      selectedLabels: [],
    };
  },
  computed: {
    selectedTags: {
      get() {
        return this.$store.state.documents.selectedTags;
      },
      set(value) {
        this.$store.commit('documents/setSelectedTags', { value });
      },
    },
    breadcrumbPath() {
      if (this.breadcrumb) {
        return this.breadcrumb;
      } else {
        const p = this.path();
        if (Array.isArray(p)) {
          return p
            .map((folderId) => {
              let folder = this.foldersById[folderId];
              return folder ? folder.name : '';
            })
            .join('/');
        }
      }
      return '';
    },
    hasTagFilter() {
      if (this.disableTagsAndLabelFiltering) {
        return false;
      }
      return this.tags.length > 0;
    },
    selectedLabelId() {
      return this.$route.query?.labelId ? parseInt(this.$route.query?.labelId) : null;
    },
    lastFilePage() {
      let startLastPage = this.files.length - 1 - ((this.files.length - 1) % this.filePageSize);
      return startLastPage / this.filePageSize;
    },
    isFilterActive() {
      return this.filter.length > 0 || this.selectedTags.length > 0;
    },
    usedTags() {
      const tagIds = [];
      const tags = [];
      if (this.filteredFilesAndFolders && Array.isArray(this.filteredFilesAndFolders)) {
        let flatten = this.flatten(this.filteredFilesAndFolders, [], this.childrenType);
        flatten.forEach((f) => {
          if (f?.tags && Array.isArray(f.tags)) {
            f.tags.forEach((t) => {
              if (!tagIds.includes(t.id)) {
                tags.push(t);
                tagIds.push(t.id);
              }
            });
          }
        });
      }
      return tags.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    },
    filteredFilesAndFolders() {
      let treeItems = [];

      if (this.currentFolder_) {
        /*
        let parentNode = this.getTreeNode(this.items, this.currentFolder_.id);
        console.warn('parentNode', parentNode);
        treeItems = parentNode?.children ? parentNode.children : [];
        console.warn('treeItems', treeItems);
        */
        treeItems = this.currentFolder_?.children ? this.currentFolder_.children : [];
      } else {
        treeItems = this.items;
      }

      if (this.disableTagsAndLabelFiltering) {
        return treeItems;
      }

      treeItems = this.selectedLabelId ? this.filterTreeBylabel(treeItems, this.selectedLabelId) : treeItems;
      treeItems = this.selectedLabelId ? this.updateAcknowledgeCount(treeItems) : treeItems;
      return treeItems;
    },
    folders() {
      if (this.isFilterVisible) {
        return [];
      }
      let lowerCasedSearchText = this.filter.toLowerCase();
      let children = this.filteredFilesAndFolders;
      if (!children) return [];

      let folders = children.filter((item) => item.type === this.folderType);

      // filter
      if (this.isFilterVisible) {
        if (lowerCasedSearchText != '') {
          let flatten = this.flatten(folders, [], [this.folderType]);
          flatten = flatten.filter((f) => f.name.toLowerCase().indexOf(lowerCasedSearchText) >= 0);
          flatten.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
          return flatten;
        } else {
          return [];
        }
      }

      return folders;
    },
    files() {
      let lowerCasedSearchText = this.filter.toLowerCase();
      let children = this.filteredFilesAndFolders;
      if (!children) return [];

      let files = children.filter((item) => this.childrenType.includes(item.type) && item._offline !== '500');
      // filter
      if (this.isFilterVisible) {
        if (lowerCasedSearchText != '' || this.selectedTags.length > 0) {
          let flatten = this.flatten(children, [], this.childrenType);
          if (this.isFilterVisible && lowerCasedSearchText != '') {
            flatten = flatten.filter((f) => f.title.toLowerCase().indexOf(lowerCasedSearchText) >= 0);
          }
          if (this.selectedTags.length > 0) {
            flatten = flatten.filter((f) => f?.tags && f.tags.some((t) => this.selectedTags.includes(t.id)));
          }
          flatten.sort((a, b) => (a.title > b.title ? 1 : b.title > a.title ? -1 : 0));
          return flatten;
        } else {
          return [];
        }
      }
      console.debug(`Sort by ${this.sorterName}`);
      return sortFunctions.sortBySorter(files, this.sorterName);
    },
    filesPage() {
      let startInd = this.filePage * this.filePageSize;
      let endInd = startInd + this.filePageSize;

      let filteredFiles = this.files;

      if (this.selectedLabelId) {
        filteredFiles = this.files.filter((file) => {
          return file?.labels ? file.labels.some((label) => label.id === this.selectedLabelId) : false;
        });
      }

      return filteredFiles.slice(startInd, endInd);
    },
    pagingText() {
      const toPage =
        this.filePage * this.filePageSize + this.filePageSize > this.files.length ? this.files.length : this.filePage * this.filePageSize + this.filePageSize;
      return `${this.filePage * this.filePageSize + 1} - ${toPage} / ${this.files.length}`;
    },
  },
  methods: {
    initiate() {
      this.$emit('onPathChanged', this.path());
    },
    headerHeight() {
      if (this.$refs['treeHeader']) {
        let h = this.$refs['treeHeader'].getBoundingClientRect();
        return h.height + 'px';
      }
      return '102px';
    },
    updateFilter(event) {
      this.filter = event.target.value;
    },
    path() {
      let path = [];
      if (this.currentFolder_) {
        let parentFolderId = this.currentFolder_[this.folderParentIdAttribute];
        path.push(this.currentFolder_.id);
        while (parentFolderId && parentFolderId !== 0) {
          path.unshift(parentFolderId);
          let folder = this.foldersById[parentFolderId];

          parentFolderId = folder ? folder[this.folderParentIdAttribute] : null;
        }
      }
      //console.warn('Current TreeSlider path', path);
      return path;
    },
    isFavorite(file) {
      return file._userState && file._userState.favorite === 1;
    },
    selectFolder(folder) {
      this.currentFolder_ = folder;
      this.showFolders = false;

      this.filePage = 0;
      this.transition = 'slide-in-right';
      setTimeout(() => {
        this.showFolders = true;
      }, 50);
      // this.$emit('onFolderSelected', folder, this.path());
      this.$emit('onPathChanged', this.path());
      this.$emit('onFolderSelected', folder.id);
    },
    selectFile(file) {
      this.$emit('onFileSelected', file?.id ? file.id : 0);
    },
    oneLevelBack() {
      if (this.currentFolder_) {
        this.transition = 'slide-in-left';
        this.showFolders = false;
        this.filePage = 0;
        this.currentFolder_ = this.foldersById[this.currentFolder_[this.folderParentIdAttribute]];
        this.$emit('onFolderSelected', this.currentFolder_?.id ? this.currentFolder_.id : 0);
        this.$emit('onPathChanged', this.path());
        setTimeout(() => {
          this.showFolders = true;
        }, 50);
      }
    },
    onToggleFilter() {
      this.filter = '';
      this.selectedTags = [];
      this.isFilterVisible = !this.isFilterVisible;
      if (this.isFilterVisible) {
        window.setTimeout(() => {
          if (this.$refs.filterInput) {
            this.$refs.filterInput.focus();
          }
        }, 100);
      }
    },
    flatten(nodes, arr, typeArr) {
      for (const node of nodes) {
        if (node.type === 'folder') {
          if (typeArr.includes('folder')) {
            arr.push(node);
          }
          if (node?.children) {
            arr.concat(this.flatten(node.children, arr, typeArr[0]));
          }
        } else if (typeArr.includes(node.type)) {
          arr.push(node);
        }
      }
      return arr;
    },
    getTreeNode(nodes, id) {
      for (const node of nodes) {
        if (node.id === id) return node;
      }
      for (const node of nodes) {
        if (node.type === 'folder' && node.children) {
          const res = this.getTreeNode(node.children, id);
          if (res) return res;
        }
      }
    },
    updateAcknowledgeCount(nodes) {
      return nodes.map((node) => {
        if (node.type == 'folder') {
          let cnt = this.countToAcknowldge(node?.children, 0);
          node._acknowledgeCount = cnt;
          if (node.children && Array.isArray(node.children) && node.children.length > 0) {
            node.children = this.updateAcknowledgeCount(node.children);
          }
        }
        return node;
      });
    },
    countToAcknowldge(children, cnt) {
      if (children) {
        for (const node of children) {
          if (node.type === 'document') {
            if (node?.quittierung && !node?._userState?.dateconfirmed) {
              cnt++;
            }
          }
          if (node.type === 'folder') {
            if (node?.children) {
              cnt += this.countToAcknowldge(node.children, cnt);
            }
          }
        }
      }
      return cnt;
    },
    filterTreeByTags(nodes, selectedTags) {
      return nodes
        .map((node) => {
          if (node?.tags) {
            const tags = node.tags.filter((tag) => selectedTags.includes(tag.id));
            return tags.length && { ...node, tags };
          } else if (node?.children && Array.isArray(node.children) && node.children.length > 0) {
            const children = this.filterTreeByTags(node.children, selectedTags);
            return children.length && { ...node, children };
          }
          return false;
        })
        .filter(Boolean);
    },
    filterTreeBylabel(nodes, labelId) {
      return nodes
        .map((node) => {
          if (node?.labels) {
            const labels = node.labels.filter((label) => label.id === labelId);
            return labels.length && { ...node, labels };
          } else if (node?.children && Array.isArray(node.children) && node.children.length > 0) {
            const children = this.filterTreeBylabel(node.children, labelId);
            return children.length && { ...node, children };
          }
          return false;
        })
        .filter(Boolean);
    },
  },
  watch: {
    currentFolder(newValue, oldValue) {
      // don't switch folder when files are selected in filter mode
      if (!this.isFilterVisible) {
        this.currentFolder_ = newValue;
        this.initiate();
      }
    },
    filter(newValue, oldValue) {
      this.expandedFileId = null;
      this.filePage = 0;
    },
    sorterName(newValue) {
      console.debug('sorterName changed', newValue);
    },
  },
};
</script>

<style scoped>
.index {
  position: relative;
  left: calc(100% - 20px);
  top: 6px;
  width: 22px;
  height: 0px;
  font-size: 8px;
}
.filter-container {
  overflow: hidden;
  width: calc(100%);
}
.filter {
  transition: transform 400ms;
  background: white;
  height: 36px;
}
.text-field:focus {
  outline: none;
}
/* NO SELECT + HIGHLIGHT COLOR ==> avoids copy/paste functions which is inappropriate on smart phones*/
* {
  user-select: none;
}
*::selection {
  background: none;
}
*::-moz-selection {
  background: none;
}
.tool-bar {
  font-size: 0.8rem;
  z-index: 2000;
  position: relative !important;
  left: 0;
  top: 0;
  transition: height 750ms;
  overflow: hidden;
  box-shadow: rgb(204, 204, 204) 0px 1px 0px;
}
.paging {
  transition: all 1s ease;
  border-radius: 20px;
  border: 1px solid #f0f0f0;
  z-index: 10000;
  position: fixed;
  height: 50px;
  max-width: 380px;
  box-shadow: #d0d0d0 3px 5px 8px;
}
</style>
